/**
 * Re-mint + reconnect recovery — the in-place equivalent of a page refresh.
 *
 * WHY THIS EXISTS (the "stuck on Reconnecting until refresh" bug)
 * --------------------------------------------------------------
 * On a real network drop (mobile airplane mode, device sleep, a long WiFi
 * outage) the SDK must re-establish the NATS WebSocket when connectivity
 * returns. The earlier recovery called `nc.reconnect()` after re-minting the
 * JWT — but `@nats-io/nats-core`'s `reconnect()` is a **no-op when the
 * connection is not currently connected**:
 *
 *     // @nats-io/nats-core@3.4.0 · lib/protocol.js
 *     reconnect() {
 *       if (this.connected) {            // ← false during an outage
 *         this.dispatchStatus({ type: "forceReconnect" });
 *         this.transport.disconnect();
 *       }
 *       return Promise.resolve();        // ← otherwise does nothing
 *     }
 *
 * So after a drop, `nc.reconnect()` returned instantly having done nothing,
 * and recovery was left entirely to nats-core's internal dial loop — which,
 * after a real mobile outage, frequently fails to re-establish the socket.
 * The connectivity FSM then hangs in `DEGRADED_NO_NATS` (the heartbeat never
 * sees a `'reconnect'`/`'ping'`) and the "Live data" indicator shows
 * "Reconnecting" forever. A page refresh fixes it because it builds a
 * brand-new connection from scratch.
 *
 * THE FIX
 * -------
 * When the socket is not demonstrably alive, rebuild a fresh connection
 * (close the wedged one, drop the connect-promise singleton, connect again)
 * — exactly what a refresh does, in-place. `connectFresh` re-mints under the
 * current identity, opens a new WebSocket, reports connect-health, and swaps
 * the active client so the heartbeat re-installs on the live status stream;
 * the subject registry replays its current interest onto the new wire filter.
 *
 * When the socket IS still alive (a sub-second blip nats-core already healed,
 * or an online edge that fired without the WS ever dropping), a full rebuild
 * is wasteful churn — so we only reconcile missed events (`refetchRealtime`).
 *
 * Pure + dependency-injected so it is unit-testable without React or a real
 * nats-core connection (see `tests/integration/connectivity/airplane-
 * reconnect.test.ts`).
 */

import type { ColdConnectRetryReason } from '../../nats/connect/cold-connect-retry.js';
import { markIntentionalClose } from '../../nats/connect/intentional-close.js';

/** Minimal nats-core connection surface this module reads. */
export interface ReconnectableConnection {
  isClosed(): boolean;
  /** `undefined` while disconnected/closed; the `ServerInfo` while connected. */
  readonly info?: unknown;
  close(): Promise<void>;
}

export interface RemintReconnectDeps {
  /** The globally-pinned live NATS connection, or `null` before first connect. */
  readonly getLiveNc: () => ReconnectableConnection | null;
  /**
   * Build a brand-new connection under the CURRENT identity. On the
   * self-mint path this re-mints a fresh JWT first; it pins the new client,
   * reports connect-health, and triggers the heartbeat re-install.
   */
  readonly connectFresh: () => Promise<void>;
  /** Drop the connect-promise singleton so `connectFresh` opens a new socket. */
  readonly resetConnectionSingleton: () => void;
  /** Reconcile events missed across the gap (refetch realtime queries). */
  readonly refetchRealtime: () => Promise<void>;
  /**
   * Report a hard connect failure (telemetry + drives the FSM unhealthy).
   * Returns the classified reason so the caller can arm the matching retry.
   */
  readonly reportFailure: (err: unknown, cause: string) => ColdConnectRetryReason;
  /** Arm the cold-connect retry ladder with the given retry thunk. */
  readonly armRetry: (reason: ColdConnectRetryReason, performRetry: () => Promise<void>) => void;
}

/**
 * `true` when the connection currently has a live handshake — not closed and
 * holding server `INFO`. nats-core clears `info` at the start of every
 * (re)dial, so `info !== undefined` is a reliable "connected right now" proxy.
 */
export function isSocketLive(nc: ReconnectableConnection): boolean {
  return !nc.isClosed() && nc.info !== undefined;
}

/**
 * Build the recovery function invoked on the online edge, app-resume, and
 * bfcache restore. Idempotent-friendly: safe to call repeatedly as edges fire.
 */
export function createRemintReconnect(deps: RemintReconnectDeps): (cause: string) => Promise<void> {
  const hardReconnect = async (): Promise<void> => {
    const stale = deps.getLiveNc();
    // Stop the wedged connection's background dial loop so it can't reconnect
    // behind us and fight the fresh socket. Fire-and-forget — a hung close
    // must never block recovery; the singleton reset wipes the reference.
    // Marked as ours first so the `closed()` watcher does not start a
    // second rebuild when this close lands.
    if (stale !== null) {
      markIntentionalClose(stale);
      void stale.close().catch((): void => {});
    }
    deps.resetConnectionSingleton();
    await deps.connectFresh();
    await deps.refetchRealtime();
  };

  // Single-flight: the online edge, an app resume and the closed() watcher
  // routinely fire together; they must share one rebuild, not race three.
  let inFlight: Promise<void> | null = null;

  return function runReconnectWithRemint(cause: string): Promise<void> {
    if (inFlight !== null) return inFlight;
    const run = (async (): Promise<void> => {
      const nc = deps.getLiveNc();
      // Socket already healthy (nats-core self-healed, or it never dropped) —
      // a rebuild would be needless churn. Just reconcile the gap.
      if (nc !== null && isSocketLive(nc)) {
        await deps.refetchRealtime();
        return;
      }
      try {
        await hardReconnect();
      } catch (err: unknown) {
        const reason = deps.reportFailure(err, cause);
        deps.armRetry(reason, hardReconnect);
      }
    })();
    const chained: Promise<void> = run.finally((): void => {
      if (inFlight === chained) inFlight = null;
    });
    inFlight = chained;
    return chained;
  };
}
